var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Children, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Animated, Easing, Pressable, View } from 'react-native';
import { BLACK_700, BLACK_900 } from '../../constants';
import { useBreakpoint } from '../../hooks';
import { StateConfigContext } from '../../providers';
import { checkIsPlatform, getTestId, isValidObject } from '../../utils';
import { Card } from '../card';
import { DynamicSvg } from '../dynamic-svg';
import { InfoTag } from '../info-tag';
import { Typography } from '../typography';
import { getCollapsibleProps, getCollapsibleStyles } from './CollapsibleStyles';
import { CollapsibleContent } from './components/CollapsibleContent';
import { LinkListItem } from '../link-list-item';
import { Divider } from '../divider';
export var Collapsible = forwardRef((props, ref) => {
  var _a, _b;
  var stateContext = useContext(StateConfigContext);
  var collapsibleProps = getCollapsibleProps(props, stateContext);
  var theme = collapsibleProps.theme,
    _collapsibleProps$lev = collapsibleProps.levelType,
    levelType = _collapsibleProps$lev === void 0 ? 'L1' : _collapsibleProps$lev,
    _collapsibleProps$wit = collapsibleProps.withCard,
    withCard = _collapsibleProps$wit === void 0 ? false : _collapsibleProps$wit,
    showInfoTagOnCard = collapsibleProps.showInfoTagOnCard,
    cardInfoTagProps = collapsibleProps.cardInfoTagProps,
    initialOpen = collapsibleProps.initialOpen,
    _isExpanded = collapsibleProps.isExpanded,
    showIcon = collapsibleProps.showIcon,
    title = collapsibleProps.title,
    titleComponent = collapsibleProps.titleComponent,
    _collapsibleProps$use = collapsibleProps.useLinkList,
    useLinkList = _collapsibleProps$use === void 0 ? false : _collapsibleProps$use,
    linkListProps = collapsibleProps.linkListProps,
    iconSize = collapsibleProps.iconSize,
    iconColor = collapsibleProps.iconColor,
    style = collapsibleProps.style,
    iconContainerStyles = collapsibleProps.iconContainerStyles,
    onToggleHandler = collapsibleProps.onToggleHandler,
    config = collapsibleProps.config,
    prefixIconName = collapsibleProps.prefixIconName,
    prefixIconProps = collapsibleProps.prefixIconProps,
    _collapsibleProps$ico = collapsibleProps.iconName,
    iconName = _collapsibleProps$ico === void 0 ? 'DownChevronIcon' : _collapsibleProps$ico,
    onPress = collapsibleProps.onPress,
    _collapsibleProps$dis = collapsibleProps.disabled,
    disabled = _collapsibleProps$dis === void 0 ? false : _collapsibleProps$dis,
    _collapsibleProps$dis2 = collapsibleProps.disableToggle,
    disableToggle = _collapsibleProps$dis2 === void 0 ? false : _collapsibleProps$dis2,
    subTitleComponent = collapsibleProps.subTitleComponent,
    titleContainerStyles = collapsibleProps.titleContainerStyles,
    subTitle = collapsibleProps.subTitle,
    subTitleContainerStyles = collapsibleProps.subTitleContainerStyles,
    infoTagProps = collapsibleProps.infoTagProps,
    cardWrapperProps = collapsibleProps.cardWrapperProps,
    _collapsibleProps$tes = collapsibleProps.testID,
    testID = _collapsibleProps$tes === void 0 ? 'Collapsible' : _collapsibleProps$tes,
    _collapsibleProps$sho = collapsibleProps.showParentBottomDividerOnExpand,
    showParentBottomDividerOnExpand = _collapsibleProps$sho === void 0 ? false : _collapsibleProps$sho,
    children = collapsibleProps.children,
    collapsibleWrapperStyles = collapsibleProps.collapsibleWrapperStyles,
    titleAccessibilityProps = collapsibleProps.titleAccessibilityProps,
    subTitleAccessibilityProps = collapsibleProps.subTitleAccessibilityProps,
    restProps = __rest(collapsibleProps, ["theme", "levelType", "withCard", "showInfoTagOnCard", "cardInfoTagProps", "initialOpen", "isExpanded", "showIcon", "title", "titleComponent", "useLinkList", "linkListProps", "iconSize", "iconColor", "style", "iconContainerStyles", "onToggleHandler", "config", "prefixIconName", "prefixIconProps", "iconName", "onPress", "disabled", "disableToggle", "subTitleComponent", "titleContainerStyles", "subTitle", "subTitleContainerStyles", "infoTagProps", "cardWrapperProps", "testID", "showParentBottomDividerOnExpand", "children", "collapsibleWrapperStyles", "titleAccessibilityProps", "subTitleAccessibilityProps"]);
  var componentRef = useRef(null);
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var animationRotation = useRef(new Animated.Value(initialOpen || isExpanded ? 1 : 0));
  var isRendered = useRef(false);
  var animationRotationRef = useRef(null);
  var requestAnimationFrameRef = useRef(null);
  var collapsibleStyles = getCollapsibleStyles(collapsibleProps, isExpanded);
  var timerRef = useRef(null);
  var ANIMATION_DURATION = 300;
  var hasChildren = Children.count(children) > 0;
  useEffect(() => {
    return () => {
      var _a, _b;
      if (requestAnimationFrameRef.current) {
        cancelAnimationFrame(requestAnimationFrameRef.current);
      }
      (_a = animationRotationRef.current) === null || _a === void 0 ? void 0 : _a.stop();
      (_b = animationRotation.current) === null || _b === void 0 ? void 0 : _b.stopAnimation();
    };
  }, []);
  useImperativeHandle(ref, () => ({
    componentRef: componentRef.current,
    isExpanded,
    toggle: startAnimation
  }), [isExpanded]);
  useEffect(() => {
    var value = initialOpen ? initialOpen : _isExpanded;
    if (value !== undefined) startAnimation(value);
  }, [_isExpanded]);
  var animateCollapse = isExpanded => {
    requestAnimationFrameRef.current = requestAnimationFrame(() => {
      var _a;
      (_a = animationRotation.current) === null || _a === void 0 ? void 0 : _a.stopAnimation();
      animationRotationRef.current = Animated.timing(animationRotation.current, {
        toValue: isExpanded ? 1 : 0,
        duration: ANIMATION_DURATION,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: !checkIsPlatform('web')
      }).start(() => {
        if (!(initialOpen && (isRendered === null || isRendered === void 0 ? void 0 : isRendered.current) == false)) onToggleHandler === null || onToggleHandler === void 0 ? void 0 : onToggleHandler(isExpanded);
        isRendered.current = true;
      });
    });
  };
  var startAnimation = expandFromToggle => {
    if (!hasChildren) return;
    if (!checkIsPlatform('web')) {
      if (timerRef.current !== null) return;
      timerRef.current = setTimeout(() => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }, ANIMATION_DURATION);
    }
    setIsExpanded(prev => {
      var actualValue = expandFromToggle != undefined ? expandFromToggle : !prev;
      animateCollapse(actualValue);
      return actualValue;
    });
  };
  var animatedStyle = {
    transform: [{
      rotate: animationRotation.current.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '-180deg']
      })
    }]
  };
  var isL1Type = levelType === 'L1';
  var _iconSize = (_b = (_a = config === null || config === void 0 ? void 0 : config.size) === null || _a === void 0 ? void 0 : _a[iconSize]) === null || _b === void 0 ? void 0 : _b.iconSize;
  var Wrapper = withCard ? Card : View;
  var l1TitleTypoType = "body-para-" + (isLargeScreen ? 'xl' : 'md') + "-bold";
  var l1SubTitleTypoType = "body-para-" + (isLargeScreen ? 'sm' : 'xs');
  var l2TitleTypoType = "body-para-" + (isLargeScreen ? 'sm' : 'sm') + (isExpanded ? '-bold' : '');
  var l2SubTitleTypoType = "body-para-" + (isLargeScreen ? 'xs' : 'xs');
  var handlePress = event => {
    if (!disabled) {
      var data = {
        event,
        collapsibleProps
      };
      if (!disableToggle) startAnimation();
      onPress === null || onPress === void 0 ? void 0 : onPress(data);
    }
  };
  return _jsxs(_Fragment, {
    children: [showInfoTagOnCard && _jsx(InfoTag, Object.assign({
      type: "on-card"
    }, cardInfoTagProps, {
      style: [collapsibleStyles.cardInfoTag, cardInfoTagProps.style]
    }, getTestId(testID, 'InfoTag'))), _jsxs(Wrapper, Object.assign({}, withCard && Object.assign({
      theme,
      noPadding: true
    }, cardWrapperProps), {
      ref: componentRef,
      style: [collapsibleStyles.wrapper, collapsibleWrapperStyles]
    }, getTestId(testID, 'wrapper'), {
      children: [_jsxs(Pressable, Object.assign({
        onPress: handlePress,
        style: [collapsibleStyles.container, style],
        accessible: true
      }, restProps, getTestId(testID), {
        accessibilityLabel: isExpanded ? restProps === null || restProps === void 0 ? void 0 : restProps.expandedStateAccessibilityLabel : restProps === null || restProps === void 0 ? void 0 : restProps.collapsedStateAccessibilityLabel
      }, checkIsPlatform('web') && {
        tabIndex: 1
      }, {
        children: [!!prefixIconName && _jsx(DynamicSvg, Object.assign({
          iconName: prefixIconName,
          fontSize: isLargeScreen ? 28 : 24,
          color: iconColor,
          style: collapsibleStyles.prefixIconName
        }, prefixIconProps, getTestId(testID, prefixIconName || 'prefixIconName'))), _jsxs(View, Object.assign({
          style: collapsibleStyles.contentContainer
        }, {
          children: [_jsxs(View, Object.assign({
            style: [collapsibleStyles.titleContainer, titleContainerStyles]
          }, {
            children: [useLinkList && _jsx(View, Object.assign({
              style: collapsibleStyles.titleStyle
            }, {
              children: _jsx(LinkListItem, Object.assign({}, linkListProps, {
                noHoverEffect: true,
                noPressEffect: true,
                noPadding: true,
                theme: theme,
                onPress: handlePress
              }, getTestId(testID, 'linkListItem')))
            })), !!titleComponent && titleComponent, !!title && _jsx(Typography, {
              type: isL1Type ? l1TitleTypoType : l2TitleTypoType,
              text: title,
              color: BLACK_900,
              style: collapsibleStyles.title,
              accessibilityProps: titleAccessibilityProps
            }), isValidObject(infoTagProps) && _jsx(InfoTag, Object.assign({}, infoTagProps, {
              size: isLargeScreen && isL1Type ? 'md' : 'sm',
              theme: theme,
              style: collapsibleStyles.infoTag
            })), showIcon && _jsx(Animated.View, Object.assign({
              style: [collapsibleStyles.iconContainer, iconContainerStyles, animatedStyle]
            }, {
              children: !!iconName && _jsx(DynamicSvg, Object.assign({
                iconName: iconName,
                fontSize: _iconSize,
                color: iconColor
              }, getTestId(testID, iconName || 'downChevronIcon')))
            }))]
          })), !!subTitleComponent && _jsx(View, Object.assign({
            style: [collapsibleStyles.subTitleComponent, subTitleContainerStyles]
          }, {
            children: subTitleComponent
          })), !!subTitle && _jsx(Typography, {
            type: isL1Type ? l1SubTitleTypoType : l2SubTitleTypoType,
            text: subTitle,
            color: BLACK_700,
            style: collapsibleStyles.subTitle,
            accessibilityProps: subTitleAccessibilityProps
          })]
        }))]
      })), isExpanded && showParentBottomDividerOnExpand && _jsx(Divider, {}), hasChildren && _jsx(CollapsibleContent, Object.assign({}, collapsibleProps, {
        collapseContainerProps: Object.assign(Object.assign({}, collapsibleProps === null || collapsibleProps === void 0 ? void 0 : collapsibleProps.collapseContainerProps), checkIsPlatform('web') && {
          'aria-hidden': !isExpanded
        }),
        isExpanded: isExpanded
      }, getTestId(testID, 'collapsibleContent')))]
    }))]
  });
});